import { TrendingDown, TrendingUp } from 'lucide-react';
import { useRecoilValue } from 'recoil';

import { Icon } from '@/components/icon';
import { Text } from '@/components/text';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { LoadingIndicator } from '@/components/ui/loading/indicator';
import { formatMarketRateModelData } from '@/lib/models/rates/format-market-rate-data';
import { MarketRateQueryModel } from '@/lib/models/rates/types';
import { getMarketRatesByRouteState, getMarketRatesSearchingState } from '@/state/rates';
import { SegmentModeType, XenetaRatesFieldApiType } from '@/types/api-types';
import { createMarketRatesKey } from '@/utils/rates';
import { tv } from '@/utils/styles';

import { RatesMarketRatePopout } from '../market-rate-popout';

export interface RatesMarketRateIndicatorProps {
  mode: SegmentModeType;
  fromId: string;
  toId: string;
  fromPortName?: string;
  toPortName?: string;
  rateFields?: XenetaRatesFieldApiType;
  upgradeRequired?: boolean;
  isNotSignedIn?: boolean;
  canSaveRates?: boolean;
}

const RatesMarketRateIndicator = ({
  mode,
  fromId,
  toId,
  fromPortName = '',
  toPortName = '',
  rateFields,
  upgradeRequired,
  isNotSignedIn,
  canSaveRates = false,
}: RatesMarketRateIndicatorProps) => {
  const { fullKey, additionalFieldsKey } = createMarketRatesKey({
    ports: [{ pol: fromId, pod: toId }],
    additionalFields: rateFields,
  });

  const rateQuery: MarketRateQueryModel = {
    id: fullKey,
    mode,
    ports: [{ pol: fromId, pod: toId }],
    additionalFields: rateFields,
    label: `${fromPortName} to ${toPortName}`,
  };

  const rates = useRecoilValue(getMarketRatesByRouteState({ pol: fromId, pod: toId, additionalFieldsKey }));
  const searchingState = useRecoilValue(getMarketRatesSearchingState({ key: fullKey }));
  const { isSearching } = searchingState || {};

  const { priceString, percentString, percentChange, unitString, hasRates } = formatMarketRateModelData(
    rates,
    mode,
    true,
  );

  const { base, bg, content, primaryText, textUnderline, subTextWrapper, subText } = styles();

  return (
    <div className={base()}>
      <span className={bg()} />
      <HoverCard openDelay={300}>
        <div className={content()}>
          {isSearching && !hasRates ? (
            <div className="mr-1.5">
              <LoadingIndicator className="relative -left-1.5 -top-0.5 mr-0" darkTheme />
              <Text className="m-0 text-xs text-grey-400">Fetching rates</Text>
            </div>
          ) : (
            <HoverCardTrigger>
              {hasRates ? (
                <Text className={primaryText()}>
                  <span className={textUnderline()}>
                    {priceString} <small>{unitString}</small>
                  </span>
                </Text>
              ) : (
                <Text className={primaryText({ className: 'text-grey-400' })}>{priceString}</Text>
              )}
              <div className={subTextWrapper()}>
                {!upgradeRequired ? (
                  <Text variant="sm" className={subText()}>
                    {percentChange > 0 && <TrendingUp className="mr-1.5 size-3 text-red-500" />}
                    {percentChange < 0 && <TrendingDown className="mr-1.5 size-3 text-green-500" />}
                    {percentString} {percentChange === 0 && hasRates ? 'change' : ''}
                  </Text>
                ) : (
                  <Text variant="sm" className={subText({ className: 'flex items-center gap-1' })}>
                    <Icon name="lock" size={3} color="states.locked" /> Rates N/A
                  </Text>
                )}
              </div>
            </HoverCardTrigger>
          )}
          <HoverCardContent>
            <RatesMarketRatePopout
              mode={mode}
              rates={rates}
              upgradeRequired={upgradeRequired}
              isNotSignedIn={isNotSignedIn}
              rateQuery={rateQuery}
              canSaveRates={canSaveRates}
            />
          </HoverCardContent>
        </div>
      </HoverCard>
    </div>
  );
};

const styles = tv({
  slots: {
    base: 'group/indicator relative',
    content: 'relative z-2 flex items-center leading-[14px]',
    bg: 'absolute -bottom-1.5 -left-2 -top-1.5 right-0 z-1 rounded-md group-hover/indicator:bg-grey-50',
    primaryText: 'relative m-0 whitespace-nowrap text-baseSm font-medium text-text-primary',
    subTextWrapper: 'mt-1.5 flex items-center',
    subText: 'm-0 whitespace-nowrap text-xs text-text-secondary',
    textUnderline:
      'relative after:absolute after:-bottom-0.5 after:left-0 after:right-0 after:border-b after:border-dashed after:border-grey-300 after:content-[""]',
  },
});

export { RatesMarketRateIndicator };
